import { Button } from "@progress/kendo-react-buttons";
import React, { useEffect, useRef, useState } from "react";
import { ReasonCodeInstance } from "../../../types/call/CallResultReasonCode";
import { CallResult } from "../../../types/call";
import { Scorecard } from "../../../types/scorecard";
import { AutoQARule, NewAutoQARule } from "../../../types/automatedQA/AutomatedQA";
import CustomMultiSelect from "../../../components/custom/form/CustomMultiSelect";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import CustomComboBox from "../../../components/custom/form/CustomComboBox";
import reasonCodeService from "../../../services/reasonCode.service";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { listNoDataRenderReasonCode } from "../../../utils/listNoDataRender";
import useTranslation from "../../../hooks/useTranslation";
import useLocale from "../../../hooks/useLocale";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { TinyUser } from "../../../types/user";
import { concatUsername, getRandomProfileColor, getUserInitials } from "../../../utils/profileUtils";
import CustomUserGridInfo from "../../../components/custom/grid/CustomUserGridInfo";

interface NewAutoQARuleProps {
  callResults: CallResult[];
  scorecards: Scorecard[];
  handleUpsertAutoQARuleSubmit: (data: NewAutoQARule, autoQARuleId?: number) => Promise<void>
  updateAutomatedQARuleData: AutoQARule | undefined;
  loader: boolean;
  onCloseUpsertAutoQARuleModal: () => void;
}

const UpsertAutoQARule: React.FC<NewAutoQARuleProps> = ({
  callResults,
  scorecards,
  handleUpsertAutoQARuleSubmit,
  updateAutomatedQARuleData,
  loader,
  onCloseUpsertAutoQARuleModal
}) => {
  const trans = useTranslation("UpsertAutoQARule");
  const localeCtx = useLocale();
  const formRef = useRef<any>();

  const [reasonCodes, setReasonCodes] = useState<ReasonCodeInstance[] | undefined>([]);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [allUsersListDialog, setAllUsersListDialog] = useState<boolean>(false);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["UpsertAutoQARule"]) {
      trans.fetchTranslations("UpsertAutoQARule");
    }
  }, [localeCtx?.selectedLocale]); 

  useEffect(() => {
    fetchReasonCodesBasedOnCallResult(updateAutomatedQARuleData?.callResult.id ?? 0)
  }, [updateAutomatedQARuleData])

  useEffect(() => {
    compareChanges();
  }, [updateAutomatedQARuleData])

  const submitHandler = (e: any) => {
    e.preventDefault();
    const form = formRef.current as Form;
    const rCodes = form.values.reasonCodeIds.map((rc: ReasonCodeInstance) => {
      return rc.id;
    });
    const callResultId = form.values.callResultId.id;
    const scorecardId = form.values.scorecardId.id;
    const newAutoQARuleData: NewAutoQARule = {
      name: updateAutomatedQARuleData ? updateAutomatedQARuleData.name : undefined,
      reasonCodeIds: rCodes,
      callResultId: callResultId,
      scorecardId: scorecardId,
    };
    updateAutomatedQARuleData ? handleUpsertAutoQARuleSubmit(newAutoQARuleData, updateAutomatedQARuleData.id) : handleUpsertAutoQARuleSubmit(newAutoQARuleData, undefined);
  };

  const fetchReasonCodesBasedOnCallResult = async (callResultId: number, state?: string) => {
    setReasonCodes(undefined);
    try {
      const data = await reasonCodeService.getReasonCodesForOutcome(callResultId);
      if (state === "empty") {
        formRef.current.onChange("reasonCodeIds", { value: [] });
      }
      setReasonCodes(data.filter((d: ReasonCodeInstance) => !d.isArchived));
    } catch (err) {
      setReasonCodes([]);
    }
  }

  const compareArray = (array1: any[], array2: any[]): boolean => {
    array1.sort(function (a, b) {
      if (a.id > b.id) return 1;
      else return -1;
    });

    array2.sort(function (a, b) {
      if (a.id > b.id) return 1;
      else return -1;
    });

    if (array1.length != array2.length) return false;
    else {
      for (let i = 0; i < array1.length; i++) {
        if (array1[i].id != array2[i].id) return false;
      }
      return true;
    }
  };

  const compareChanges = () => {
    const form = formRef.current as Form;
    if (updateAutomatedQARuleData) {
      if (
        compareArray(
          updateAutomatedQARuleData.reasonCodes
            ?.map((rc) => {
              return rc;
            })
            .slice() ?? [],
          form.values.reasonCodeIds
        ) &&
        form.values.callResultId.text ==
        updateAutomatedQARuleData.callResult.text &&
        form.values.scorecardId.scorecardName ==
        updateAutomatedQARuleData.scoreCard.scorecardName
      ) {
        setIsChanged(false);
      } else {
        setIsChanged(true);
      }
    }
  }
  
  const OutcomeValidator = (value: string) => {
    if (!value) {
      return trans.fetchLabelKeyTranslation("ValidationTextThree","Please select an outcome");
    }
    return ""
  }
  
  const ScorecardValidator = (value: string) => {
    if (!value) {
      return trans.fetchLabelKeyTranslation("ValidationTextFour","Please select a scorecard");
    }
    return ""
  }

  return (
    <Form
      ref={formRef}
      initialValues={{
        callResultId: updateAutomatedQARuleData ? updateAutomatedQARuleData.callResult : undefined,
        reasonCodeIds: updateAutomatedQARuleData ? updateAutomatedQARuleData.reasonCodes : [],
        scorecardId: updateAutomatedQARuleData ? updateAutomatedQARuleData.scoreCard : undefined,
      }}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement>
          <Dialog title={updateAutomatedQARuleData ? trans.fetchLabelKeyTranslation("EditAllocationCriteriaTitle","Edit Scorecard Mapping") : trans.fetchLabelKeyTranslation("NewAllocationCriteriaTitle","Map Scorecard to Outcome")} onClose={onCloseUpsertAutoQARuleModal}>
            <div className="vi-modal-lg">
              <div className="mb-3">
                <label>{trans.fetchLabelKeyTranslation("OutcomeFieldTitle","Select Outcome")}</label>
                <Field
                  id="callResultId"
                  name="callResultId"
                  data={callResults}
                  value={formRenderProps.valueGetter("callResultId")}
                  textField="text"
                  filterable={true}
                  component={CustomComboBox}
                  validator={OutcomeValidator}
                  onChange={(e: ComboBoxChangeEvent) => {
                    if (e.value == null) {
                      formRenderProps.onChange("reasonCodeIds", { value: [] })
                      setReasonCodes([])
                    } else {
                      fetchReasonCodesBasedOnCallResult(e.value.id, "empty");
                    }
                    compareChanges();
                  }}
                />
              </div>
              
              <div className="mb-3">
                <label>{trans.fetchLabelKeyTranslation("ReasonCodeFieldTitle","Select Reason Codes")}</label>
                <Field
                  dataItemKey="id"
                  id="reasonCodeIds"
                  name="reasonCodeIds"
                  data={reasonCodes}
                  value={formRenderProps.valueGetter("reasonCodes")}
                  textField="name"
                  filtering={true}
                  component={CustomMultiSelect}
                  disabled={reasonCodes == undefined || !formRenderProps.valueGetter("callResultId")}
                  onChange={compareChanges}
                  placeholder={!formRenderProps.valueGetter("callResultId") ? trans.fetchLabelKeyTranslation("ReasonCodesFieldPlaceholder","Select an outcome to see reason codes") : reasonCodes?.length!==0 ? trans.fetchLabelKeyTranslation("AllReasonCodesText","All Reason Codes") : ""}
                  listNoDataRender={(element:React.ReactElement<HTMLDivElement>)=>listNoDataRenderReasonCode(element, trans.fetchLabelKeyTranslation("ReasonCodesNotAvailableText","No reason codes are available for this outcome"))}
                />
              </div>

              <div className="mb-3">
                <label>{trans.fetchLabelKeyTranslation("ScorecardFieldTitle","Select Scorecard")}</label>
                <Field
                  id="scorecardId"
                  name="scorecardId"
                  data={scorecards}
                  value={formRenderProps.valueGetter("scorecardId")}
                  textField="scorecardName"
                  filterable={true}
                  component={CustomComboBox}
                  validator={ScorecardValidator}
                  onChange={compareChanges}
                />
              </div>
              
              {updateAutomatedQARuleData && updateAutomatedQARuleData.allocatedUsers.length>0 && (
                <div className="tblUsr moreUsr users-small mb-2 pl-0">
                  <p className="mb-0 assigned-users mb-1">{trans.fetchLabelKeyTranslation("ModifyingMappingImpactWarning","The existing mapping has been enabled for the following users.")} </p>
                  <div className="ml-1">

                  {updateAutomatedQARuleData.allocatedUsers.slice(0, 8).map((user: TinyUser) => {
                    const profileColor = getRandomProfileColor(user?.id);
                    const userName = concatUsername(user?.firstName, user?.lastName).trim();
                    let initials = getUserInitials(user?.firstName, user?.lastName);
                    initials = initials !== "" ? initials : getUserInitials(user?.email, "");
      
                    return (
                      <div className="topUsrAreaPic zoomPic" key={user?.id}>
                        <div className="topUsrAreaPic-i">
                          <div
                            className={`contact-list-icon cursor-pointer ${profileColor}`}
                            title={userName === "" ? user.email : userName}
                          >
                            <div className="contact-list-icon-txt">{initials}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                    {updateAutomatedQARuleData.allocatedUsers.length > 8 && (
                    <div
                      className="topUsrAreaPic zoomPic"
                      onClick={() => setAllUsersListDialog(true)}
                    >
                      <div className="topUsrAreaPic-i">
                        <div className="contact-list-icon cursor-pointer bg-darksteelblue">
                          <div className="contact-list-icon-txt">
                            +{updateAutomatedQARuleData.allocatedUsers.length - 8}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
      
                  

                  {allUsersListDialog && (
                    <Dialog title={trans.fetchLabelKeyTranslation("AllAssignedUsersText","All Assigned Users")} onClose={() => setAllUsersListDialog(false)}>
                      {updateAutomatedQARuleData.allocatedUsers.map((user: TinyUser) => (
                        <div className="tblUsr w-100" key={user.id}>
                          <div className="topUsrAreaPic mb-2" style={{ marginBottom: "0px", minWidth: "350px" }}>
                            <div className="topUsrAreaPic-i">
                              <CustomUserGridInfo userInfo={user} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <DialogActionsBar>
                        <Button className={`btn bg-black-5`} onClick={() => setAllUsersListDialog(false)}>
                          {trans.fetchLabelKeyTranslation("BtnCloseText","Close")}
                        </Button>
                      </DialogActionsBar>
                    </Dialog>
                  )}
                </div>
              )}
            </div>
           <DialogActionsBar>
            <Button className="btn bg-black-5 mr-2" onClick={onCloseUpsertAutoQARuleModal}>
              {trans.fetchLabelKeyTranslation("CancelBtnText","Cancel")}
            </Button>
            <Button
              className="btn bg-primary text-white me-2"
              disabled={!formRenderProps.valid || (updateAutomatedQARuleData && !isChanged) || loader}
              onClick={submitHandler}
            >
              {updateAutomatedQARuleData ? trans.fetchLabelKeyTranslation("EditBtnText","Update") : trans.fetchLabelKeyTranslation("AddBtnText","Add")}
            </Button>
          </DialogActionsBar>
        </Dialog>
      </FormElement>
      )}
    />
  );
};

export default UpsertAutoQARule;
